import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, Filter,
  AlertTriangle, Recycle, Trash2, Ban, RotateCcw, Truck,
  IndianRupee, Package, ClipboardList, Clock,
} from 'lucide-react';
import { api } from '@/utils/api';
import { loadAllPages } from '@/utils/loadAllPages';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { DispositionQueuePanel } from './DispositionQueuePanel';
import { formatINR, formatDate } from '@/utils/formatters';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Product {
  id: string;
  name: string;
  unit?: string;
}

interface ProductionOrderOption {
  id: string;
  number: string;
  productId: string;
  productName: string;
}

interface RecoveredComponent {
  productId: string;
  quantity: number;
  unit: string;
  condition: 'reusable' | 'wastage';
}

interface Rejection {
  id: string;
  number: string;
  productionOrderId: string | null;
  productionOrderNumber: string | null;
  productId: string;
  productName: string;
  quantity: number;
  unit: string;
  reason: string;
  category: string;
  disposition: string;
  recoveredComponents: RecoveredComponent[] | null;
  scrapSoldValue: number | null;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DISPOSITIONS = ['pending', 'decompose', 'scrap', 'rework', 'return_to_vendor'];

const DISPOSITION_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  pending:          { label: 'Pending',           bg: 'bg-yellow-50',  text: 'text-yellow-700' },
  decompose:        { label: 'Decomposed',        bg: 'bg-blue-50',    text: 'text-blue-700' },
  scrap:            { label: 'Scrap',             bg: 'bg-red-50',     text: 'text-red-700' },
  rework:           { label: 'Rework',            bg: 'bg-orange-50',  text: 'text-orange-700' },
  return_to_vendor: { label: 'Return to Vendor',  bg: 'bg-purple-50',  text: 'text-purple-700' },
};

const CATEGORIES = [
  'dimensional',
  'visual',
  'functional',
  'material',
  'contamination',
  'packaging',
  'other',
];

const CATEGORY_LABELS: Record<string, string> = {
  dimensional: 'Dimensional',
  visual: 'Visual',
  functional: 'Functional',
  material: 'Material',
  contamination: 'Contamination',
  packaging: 'Packaging',
  other: 'Other',
};

const CATEGORY_COLORS: Record<string, string> = {
  dimensional: 'bg-indigo-50 text-indigo-700',
  visual: 'bg-pink-50 text-pink-700',
  functional: 'bg-cyan-50 text-cyan-700',
  material: 'bg-amber-50 text-amber-700',
  contamination: 'bg-red-50 text-red-700',
  packaging: 'bg-teal-50 text-teal-700',
  other: 'bg-gray-100 text-gray-600',
};

const BLANK_COMPONENT: RecoveredComponent = {
  productId: '',
  quantity: 1,
  unit: 'pcs',
  condition: 'reusable',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function RejectionsPage() {
  const [rejections, setRejections] = useState<Rejection[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productionOrders, setProductionOrders] = useState<ProductionOrderOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poError, setPoError] = useState<string | null>(null);

  /* Filters */
  const [search, setSearch] = useState('');
  const [filterDisposition, setFilterDisposition] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  /* Record Rejection modal */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    productionOrderId: '',
    productId: '',
    quantity: 1,
    reason: '',
    category: 'other',
  });

  /* Decompose modal */
  const [showDecomposeModal, setShowDecomposeModal] = useState(false);
  const [decomposeId, setDecomposeId] = useState<string | null>(null);
  const [decomposeSaving, setDecomposeSaving] = useState(false);
  const [recoveredComponents, setRecoveredComponents] = useState<RecoveredComponent[]>([
    { ...BLANK_COMPONENT },
  ]);

  /* Scrap Sale modal */
  const [showScrapSaleModal, setShowScrapSaleModal] = useState(false);
  const [scrapSaleId, setScrapSaleId] = useState<string | null>(null);
  const [scrapSaleSaving, setScrapSaleSaving] = useState(false);
  const [scrapSoldValue, setScrapSoldValue] = useState('');

  /* ---- Fetch ---- */

  const fetchRejections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      if (filterDisposition) params.disposition = filterDisposition;
      if (filterCategory) params.category = filterCategory;
      const res = await api.get('/manufacturing/rejections', { params });
      const list = res.data.data?.rejections ?? res.data.data ?? [];
      setRejections(list);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load rejections');
    } finally {
      setLoading(false);
    }
  }, [search, filterDisposition, filterCategory, page]);

  useEffect(() => {
    fetchRejections();
    api.get('/products?limit=500').then(res => {
      setProducts(res.data.data?.products ?? res.data.data ?? []);
    }).catch(() => {});
  }, [fetchRejections]);

  // The server allows 100 a page, so walk every page; a failed load is shown, not swallowed (SUN-281).
  useEffect(() => {
    const ctl = new AbortController();
    loadAllPages<any>('/manufacturing/production-orders', ctl.signal, { label: 'Production order' })
      .then(orders => {
        setPoError(null);
        setProductionOrders(orders.map(o => ({ id: o.id, number: o.number, productId: o.productId ?? o.product?.id ?? '', productName: o.product?.name ?? o.productName ?? '' })));
      })
      .catch(err => { if (!ctl.signal.aborted) setPoError(err?.response?.data?.error || err?.message || 'Failed to load production orders'); });
    return () => ctl.abort();
  }, []);

  /* ---- Stats ---- */

  const stats = {
    total: rejections.length,
    pending: rejections.filter(r => r.disposition === 'pending').length,
    decomposed: rejections.filter(r => r.disposition === 'decompose').length,
    scrapped: rejections.filter(r => r.disposition === 'scrap').length,
    scrapValue: rejections.reduce((sum, r) => sum + (r.scrapSoldValue || 0), 0),
  };

  /* ---- Handlers ---- */

  const handleCreateRejection = async () => {
    if (!createForm.productId || !createForm.reason.trim()) return alert('Product and reason are required');
    try {
      setSaving(true);
      await api.post('/manufacturing/rejections', {
        productionOrderId: createForm.productionOrderId || undefined,
        productId: createForm.productId,
        quantity: Number(createForm.quantity) || 1,
        reason: createForm.reason.trim(),
        category: createForm.category,
      });
      setShowCreateModal(false);
      setCreateForm({ productionOrderId: '', productId: '', quantity: 1, reason: '', category: 'other' });
      fetchRejections();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to record rejection');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkScrap = async (id: string) => {
    if (!confirm('Mark this rejection as scrap? This cannot be undone.')) return;
    try {
      await api.patch(`/manufacturing/rejections/${id}/scrap-sold`, { disposition: 'scrap' });
      fetchRejections();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to mark as scrap');
    }
  };

  const openDecomposeModal = (id: string) => {
    setDecomposeId(id);
    setRecoveredComponents([{ ...BLANK_COMPONENT }]);
    setShowDecomposeModal(true);
  };

  const handleDecompose = async () => {
    if (!decomposeId) return;
    const validComponents = recoveredComponents.filter(c => c.productId && c.quantity > 0);
    if (validComponents.length === 0) return alert('Add at least one recovered component');
    try {
      setDecomposeSaving(true);
      await api.patch(`/manufacturing/rejections/${decomposeId}/decompose`, {
        recoveredComponents: validComponents,
      });
      setShowDecomposeModal(false);
      setDecomposeId(null);
      fetchRejections();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to decompose');
    } finally {
      setDecomposeSaving(false);
    }
  };

  const openScrapSaleModal = (id: string) => {
    setScrapSaleId(id);
    setScrapSoldValue('');
    setShowScrapSaleModal(true);
  };

  const handleRecordScrapSale = async () => {
    if (!scrapSaleId) return;
    const value = parseFloat(scrapSoldValue);
    if (isNaN(value) || value < 0) return alert('Enter a valid scrap sale value');
    try {
      setScrapSaleSaving(true);
      await api.patch(`/manufacturing/rejections/${scrapSaleId}/scrap-sold`, {
        scrapSoldValue: value,
      });
      setShowScrapSaleModal(false);
      setScrapSaleId(null);
      fetchRejections();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to record scrap sale');
    } finally {
      setScrapSaleSaving(false);
    }
  };

  /* ---- Recovered component helpers ---- */

  const updateRecoveredComponent = (index: number, field: keyof RecoveredComponent, value: string | number) => {
    setRecoveredComponents(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addRecoveredComponent = () => {
    setRecoveredComponents(prev => [...prev, { ...BLANK_COMPONENT }]);
  };

  const removeRecoveredComponent = (index: number) => {
    setRecoveredComponents(prev => prev.filter((_, i) => i !== index));
  };

  /* ---- Render ---- */

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Rejection Records</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Track rejected items, decomposition, and scrap disposal
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Record Rejection
        </button>
      </div>

      <DispositionQueuePanel />

      {/* Stats Cards */}
      {!loading && !error && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--saffron-50)]">
                <ClipboardList size={20} className="text-[var(--ink-0)]" />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Total Rejections</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-50">
                <Clock size={20} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Pending</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Recycle size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Decomposed</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{stats.decomposed}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
                <Ban size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Scrapped</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{stats.scrapped}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
                <IndianRupee size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Scrap Value</p>
                <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(stats.scrapValue)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search by product, reason..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
            showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : ''
          }`}
          style={!showFilters ? { borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' } : undefined}
        >
          <Filter size={16} />
          Filters
          {(filterDisposition || filterCategory) && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--ink-0)] text-[10px] text-white">
              {(filterDisposition ? 1 : 0) + (filterCategory ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border p-4"
          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}
        >
          <select
            value={filterDisposition}
            onChange={(e) => { setFilterDisposition(e.target.value); setPage(1); }}
            className="rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
          >
            <option value="">All Dispositions</option>
            {DISPOSITIONS.map(d => (
              <option key={d} value={d}>{DISPOSITION_CONFIG[d]?.label || d}</option>
            ))}
          </select>

          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
            className="rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>

          {(filterDisposition || filterCategory) && (
            <button
              onClick={() => { setFilterDisposition(''); setFilterCategory(''); setPage(1); }}
              className="flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:opacity-80"
              style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading rejections...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchRejections} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : rejections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
            <AlertTriangle size={40} className="mb-2" />
            <p className="text-sm">No rejection records found</p>
            <p className="text-xs">Rejections will appear here once recorded</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="border-b text-left text-xs font-medium uppercase tracking-wider"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-muted)' }}
                >
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Production Order</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                {rejections.map(r => {
                  const dispConf = DISPOSITION_CONFIG[r.disposition] || DISPOSITION_CONFIG.pending;
                  return (
                    <tr
                      key={r.id}
                      className="transition-colors"
                      style={{ borderColor: 'var(--border-primary)' }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-surface-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <td className="px-4 py-3 font-mono text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                        {r.number || r.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                        {r.productName}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                        {r.quantity} {r.unit || 'pcs'}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3" style={{ color: 'var(--text-secondary)' }} title={r.reason}>
                        {r.reason}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[r.category] || 'bg-gray-100 text-gray-600'}`}>
                          {CATEGORY_LABELS[r.category] || r.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${dispConf.bg} ${dispConf.text}`}>
                          {dispConf.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {r.productionOrderNumber || '-'}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(r.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {r.disposition === 'pending' && (
                            <>
                              <button
                                onClick={() => openDecomposeModal(r.id)}
                                className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                                title="Decompose into components"
                              >
                                <Recycle size={12} />
                                Decompose
                              </button>
                              <button
                                onClick={() => handleMarkScrap(r.id)}
                                className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors"
                                title="Mark as scrap"
                              >
                                <Ban size={12} />
                                Scrap
                              </button>
                            </>
                          )}
                          {r.disposition === 'scrap' && !r.scrapSoldValue && (
                            <button
                              onClick={() => openScrapSaleModal(r.id)}
                              className="flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
                              title="Record scrap sale value"
                            >
                              <IndianRupee size={12} />
                              Record Sale
                            </button>
                          )}
                          {r.disposition === 'scrap' && r.scrapSoldValue != null && r.scrapSoldValue > 0 && (
                            <span className="text-xs font-medium text-green-600">
                              Sold: {formatINR(r.scrapSoldValue)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ==================== Record Rejection Modal ==================== */}
      {showCreateModal && (
        <Modal open onClose={() => setShowCreateModal(false)} className="!max-w-lg">
          <div>
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Record Rejection</h2>
              <RequestCloseButton style={{ color: 'var(--text-muted)' }} className="hover:opacity-70">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              {/* Production Order */}
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Production Order</label>
                <select
                  value={createForm.productionOrderId}
                  onChange={(e) => {
                    // The product comes from the picked order (replaces the old one); clearing the order leaves the product alone.
                    const po = productionOrders.find(o => o.id === e.target.value);
                    setCreateForm({ ...createForm, productionOrderId: e.target.value, productId: po?.productId || createForm.productId });
                  }}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                >
                  <option value="">Select Production Order (optional)</option>
                  {productionOrders.map(po => (
                    <option key={po.id} value={po.id}>{po.number} - {po.productName}</option>
                  ))}
                </select>
                {poError && <p role="alert" className="mt-1 text-xs text-red-600">Could not load production orders: {poError}</p>}
              </div>

              {/* Product */}
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Product *</label>
                <select
                  value={createForm.productId}
                  onChange={(e) => setCreateForm({ ...createForm, productId: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                >
                  <option value="">Select Product</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Quantity + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Quantity *</label>
                  <input
                    type="number"
                    min={1}
                    value={createForm.quantity}
                    onChange={(e) => setCreateForm({ ...createForm, quantity: Number(e.target.value) || 1 })}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Category *</label>
                  <select
                    value={createForm.category}
                    onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Reason *</label>
                <textarea
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  rows={3}
                  placeholder="Describe why this batch was rejected..."
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <RequestCloseButton
                className="rounded-lg border px-4 py-2 text-sm font-medium hover:opacity-80"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleCreateRejection}
                disabled={saving || !createForm.productId || !createForm.reason.trim()}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Record Rejection
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ==================== Decompose Modal ==================== */}
      {showDecomposeModal && (
        <Modal open onClose={() => setShowDecomposeModal(false)} className="!max-w-2xl">
          <div>
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Decompose Rejection</h2>
              <RequestCloseButton style={{ color: 'var(--text-muted)' }} className="hover:opacity-70">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                List the components recovered from decomposing the rejected item(s).
              </p>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Recovered Components</label>
                <button
                  onClick={addRecoveredComponent}
                  className="flex items-center gap-1 rounded-lg bg-[var(--saffron-50)] px-3 py-1.5 text-xs font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors"
                >
                  <Plus size={12} />
                  Add Component
                </button>
              </div>

              <div className="space-y-3">
                {recoveredComponents.map((comp, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border p-3"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Component {idx + 1}</span>
                      {recoveredComponents.length > 1 && (
                        <button
                          onClick={() => removeRecoveredComponent(idx)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {/* Product */}
                      <select
                        value={comp.productId}
                        onChange={(e) => updateRecoveredComponent(idx, 'productId', e.target.value)}
                        className="rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                      >
                        <option value="">Select Product *</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>

                      {/* Quantity */}
                      <input
                        type="number"
                        min={0.01}
                        step={0.01}
                        value={comp.quantity}
                        onChange={(e) => updateRecoveredComponent(idx, 'quantity', Number(e.target.value) || 0)}
                        placeholder="Quantity"
                        className="rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                      />

                      {/* Unit */}
                      <select
                        value={comp.unit}
                        onChange={(e) => updateRecoveredComponent(idx, 'unit', e.target.value)}
                        className="rounded-lg border px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                      >
                        {['pcs', 'kg', 'g', 'ltr', 'ml', 'mtr', 'ft', 'set', 'nos'].map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>

                      {/* Condition */}
                      <div className="flex items-center gap-4 px-1">
                        <label className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <input
                            type="radio"
                            name={`condition-${idx}`}
                            value="reusable"
                            checked={comp.condition === 'reusable'}
                            onChange={() => updateRecoveredComponent(idx, 'condition', 'reusable')}
                            className="h-4 w-4 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                          />
                          Reusable
                        </label>
                        <label className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <input
                            type="radio"
                            name={`condition-${idx}`}
                            value="wastage"
                            checked={comp.condition === 'wastage'}
                            onChange={() => updateRecoveredComponent(idx, 'condition', 'wastage')}
                            className="h-4 w-4 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                          />
                          Wastage
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <RequestCloseButton
                className="rounded-lg border px-4 py-2 text-sm font-medium hover:opacity-80"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleDecompose}
                disabled={decomposeSaving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {decomposeSaving && <Loader2 size={14} className="animate-spin" />}
                <Recycle size={14} />
                Decompose
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ==================== Scrap Sale Modal ==================== */}
      {showScrapSaleModal && (
        <Modal open onClose={() => setShowScrapSaleModal(false)} className="!max-w-sm">
          <div>
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Record Scrap Sale</h2>
              <RequestCloseButton style={{ color: 'var(--text-muted)' }} className="hover:opacity-70">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Enter the value received from selling the scrap material.
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  Scrap Sale Value (INR) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    ₹
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={scrapSoldValue}
                    onChange={(e) => setScrapSoldValue(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-lg border py-2 pl-8 pr-3 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <RequestCloseButton
                className="rounded-lg border px-4 py-2 text-sm font-medium hover:opacity-80"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleRecordScrapSale}
                disabled={scrapSaleSaving || !scrapSoldValue}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {scrapSaleSaving && <Loader2 size={14} className="animate-spin" />}
                <IndianRupee size={14} />
                Record Sale
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
